import { useState, useEffect, useRef } from "react";
import { vendorApi } from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Vendor?</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Vendor Detail Drawer ── */
function VendorDrawer({ vendor, onClose, onEdit }) {
  return (
    <div className="rv-dialog-backdrop" onClick={onClose}>
      <div className="rv-vendor-drawer" onClick={e => e.stopPropagation()}>
        <div className="rv-vendor-drawer-header">
          <div className="rv-vendor-drawer-avatar">
            {vendor.vendorName?.charAt(0).toUpperCase()}
          </div>
          <div className="rv-vendor-drawer-title-wrap">
            <h3 className="rv-vendor-drawer-name">{vendor.vendorName}</h3>
            <span className={`rv-inhouse-pill ${vendor.isInHouse ? "rv-inhouse-pill--yes" : "rv-inhouse-pill--no"}`}>
              <span className="rv-status-dot" />
              {vendor.isInHouse ? "In-House" : "External"}
            </span>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-vendor-drawer-body">
          <div className="rv-vendor-detail-grid">
            <div className="rv-vendor-detail-item">
              <span className="rv-vendor-detail-label"><PhoneIcon /> Phone</span>
              <span className="rv-vendor-detail-val">{vendor.phoneNumber || "—"}</span>
            </div>
            <div className="rv-vendor-detail-item">
              <span className="rv-vendor-detail-label"><PhoneAltIcon /> Contact</span>
              <span className="rv-vendor-detail-val">{vendor.contactNumber || "—"}</span>
            </div>
            <div className="rv-vendor-detail-item rv-vendor-detail-item--full">
              <span className="rv-vendor-detail-label"><LocationIcon /> Address</span>
              <span className="rv-vendor-detail-val rv-vendor-detail-val--addr">{vendor.address || "—"}</span>
            </div>
          </div>
        </div>

        <div className="rv-vendor-drawer-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
          <button className="rv-btn rv-btn--primary" onClick={() => { onEdit(vendor); onClose(); }}>
            <EditIcon /> Edit Vendor
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Vendor Form Modal ── */
function VendorModal({ initial, onSave, onClose, saving }) {
  const isEdit = !!initial?.vendorID;
  const [form, setForm] = useState({
    vendorName: initial?.vendorName ?? "",
    isInHouse: initial?.isInHouse ?? false,
    phoneNumber: initial?.phoneNumber ?? "",
    contactNumber: initial?.contactNumber ?? "",
    address: initial?.address ?? "",
  });
  const [errors, setErrors] = useState({});

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    setErrors(e => ({ ...e, [key]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.vendorName.trim()) e.vendorName = "Vendor name is required.";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...initial, ...form });
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form rv-vendor-modal">
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><VendorIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Vendor" : "New Vendor"}</h3>
            <p className="rv-modal-sub">{isEdit ? "Update vendor details below." : "Fill in details to add a new vendor."}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body">
          {/* Vendor Name */}
          <div className="rv-field">
            <label className="rv-label">Vendor Name <span className="rv-required">*</span></label>
            <input
              className={`rv-input ${errors.vendorName ? "rv-input--err" : ""}`}
              placeholder="e.g. Mehta Gems & Jewels"
              value={form.vendorName}
              onChange={e => set("vendorName", e.target.value)}
            />
            {errors.vendorName && <p className="rv-field-err">{errors.vendorName}</p>}
          </div>

          {/* Phone + Contact — 2 col */}
          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Phone Number</label>
              <input
                className="rv-input"
                placeholder="e.g. +91 98765 43210"
                value={form.phoneNumber}
                onChange={e => set("phoneNumber", e.target.value)}
              />
            </div>
            <div className="rv-field">
              <label className="rv-label">Contact Number</label>
              <input
                className="rv-input"
                placeholder="e.g. +91 98765 43211"
                value={form.contactNumber}
                onChange={e => set("contactNumber", e.target.value)}
              />
            </div>
          </div>

          {/* Address */}
          <div className="rv-field">
            <label className="rv-label">Address</label>
            <textarea
              className="rv-input rv-textarea"
              placeholder="Shop / street / city…"
              rows={3}
              value={form.address}
              onChange={e => set("address", e.target.value)}
            />
          </div>

          {/* In-House toggle */}
          <div className="rv-field rv-field--row">
            <div>
              <label className="rv-label">In-House Vendor</label>
              <p className="rv-field-hint">Is this vendor operated within your organisation?</p>
            </div>
            <button
              type="button"
              className={`rv-toggle ${form.isInHouse ? "rv-toggle--on" : ""}`}
              onClick={() => set("isInHouse", !form.isInHouse)}
            >
              <span className="rv-toggle-knob" />
            </button>
          </div>
        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN VENDOR PAGE
══════════════════════════════════════ */
export default function VendorPage() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");

  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [toast, setToast] = useState(null);

  /* ── Resizable columns ── */
  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  // Columns: #, Vendor Name, Phone, Contact, Address, Type, Actions
  useResizableColumns(tableRef, tableReady, [50, 200, 140, 140, 220, 110, 100]);

  /* ── Fetch ── */
  const fetchAll = async () => {
    try {
      setLoading(true);
      setTableReady(false);
      const data = await vendorApi.getAll();
      setVendors(Array.isArray(data) ? data : data?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load vendors.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Stats ── */
  const inHouseCount = vendors.filter(v => v.isInHouse).length;
  const externalCount = vendors.filter(v => !v.isInHouse).length;

  /* ── Save ── */
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.vendorID) {
        await vendorApi.update(formData);
        showToast("Vendor updated successfully.");
      } else {
        await vendorApi.create(formData);
        showToast("Vendor created successfully.");
      }
      setModal(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await vendorApi.remove(confirm.id);
      showToast("Vendor deleted.");
      setConfirm(null);
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Filtered list ── */
  const filtered = vendors.filter(v => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      v.vendorName?.toLowerCase().includes(q) ||
      v.phoneNumber?.toLowerCase().includes(q) ||
      v.contactNumber?.toLowerCase().includes(q) ||
      v.address?.toLowerCase().includes(q);
    const matchType =
      filterType === "all" ||
      (filterType === "inhouse" && v.isInHouse) ||
      (filterType === "external" && !v.isInHouse);
    return matchSearch && matchType;
  });
  const {
    paged, page, totalPages, pageSize,
    setPage, setPageSize,
    pageStart, pageEnd, totalItems,
  } = usePagination(filtered, 10);

  return (
    <div className="rv-vendor-page">

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
      {confirm && (
        <ConfirmDialog
          name={confirm.name}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {modal && (
        <VendorModal
          initial={modal.data}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}
      {drawer && (
        <VendorDrawer
          vendor={drawer}
          onClose={() => setDrawer(null)}
          onEdit={v => setModal({ data: v })}
        />
      )}

      {/* ── Topbar ── */}
      <div className="rv-vendor-topbar">
        <div className="rv-vendor-topbar-left">
          <h2 className="rv-vendor-heading">Vendors</h2>
          <span className="rv-vendor-count">{vendors.length} total</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> New Vendor
        </button>
      </div>

      {/* ── Stat pills ── */}
      {/* {!loading && vendors.length > 0 && (
        <div className="rv-vendor-stats">
          <div className="rv-vendor-stat-pill">
            <div className="rv-vendor-stat-icon rv-vendor-stat-icon--inhouse"><BuildingIcon /></div>
            <div>
              <p className="rv-vendor-stat-label">In-House</p>
              <p className="rv-vendor-stat-val">{inHouseCount}</p>
            </div>
          </div>
          <div className="rv-vendor-stat-pill">
            <div className="rv-vendor-stat-icon rv-vendor-stat-icon--ext"><TruckIcon /></div>
            <div>
              <p className="rv-vendor-stat-label">External</p>
              <p className="rv-vendor-stat-val">{externalCount}</p>
            </div>
          </div>
        </div>
      )} */}

      {/* ── Filters ── */}
      <div className="rv-vendor-filters">
        <div className="rv-vendor-search">
          <SearchIcon />
          <input
            placeholder="Search by name, phone or address…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-vendor-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>

        <div className="rv-filter-tabs">
          {[
            { value: "all", label: "All" },
            { value: "inhouse", label: "In-House" },
            { value: "external", label: "External" },
          ].map(s => (
            <button
              key={s.value}
              className={`rv-filter-tab ${filterType === s.value ? "rv-filter-tab--active" : ""}`}
              onClick={() => { setFilterType(s.value); setPage(1); }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="rv-card rv-vendor-card">
        {loading ? (
          <div className="rv-vendor-empty">
            <div className="rv-loader" />
            <p>Loading vendors…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rv-vendor-empty">
            <div className="rv-empty-icon"><VendorIcon /></div>
            <p className="rv-empty-title">No vendors found</p>
            <p className="rv-empty-sub">
              {search ? "Try a different search term." : "Add your first vendor to get started."}
            </p>
            {!search && (
              <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                <PlusIcon /> New Vendor
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="rv-table-wrap rv-vendor-table-wrap">
              <table className="rv-table rv-vendor-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Vendor Name</th>
                    <th>Phone</th>
                    <th>Contact</th>
                    <th>Address</th>
                    <th>Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((v, i) => (
                    <tr
                      key={v.vendorID}
                      className="rv-vendor-row"
                      onClick={() => setDrawer(v)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="rv-vendor-idx">{pageStart + i}</td>

                      {/* Name */}
                      <td>
                        <div className="rv-vendor-name-cell">
                          {/* <div className="rv-vendor-avatar-sm">
                          {v.vendorName?.charAt(0).toUpperCase()}
                        </div> */}
                          <span className="rv-vendor-name">{v.vendorName}</span>
                        </div>
                      </td>

                      {/* Phone */}
                      <td>
                        <span className="rv-vendor-phone">{v.phoneNumber || <span className="rv-empty-val">—</span>}</span>
                      </td>

                      {/* Contact */}
                      <td>
                        <span className="rv-vendor-phone">{v.contactNumber || <span className="rv-empty-val">—</span>}</span>
                      </td>

                      {/* Address */}
                      <td>
                        <span className="rv-vendor-addr">{v.address || <span className="rv-empty-val">—</span>}</span>
                      </td>

                      {/* Type */}
                      <td>
                        <span className={`rv-inhouse-pill ${v.isInHouse ? "rv-inhouse-pill--yes" : "rv-inhouse-pill--no"}`}>
                          {/* <span className="rv-status-dot" /> */}
                          {v.isInHouse ? "In-House" : "External"}
                        </span>
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="rv-actions" onClick={e => e.stopPropagation()}>
                          <button
                            className="rv-action-btn rv-action-btn--edit"
                            title="Edit"
                            onClick={() => setModal({ data: v })}
                          >
                            <EditIcon />
                          </button>
                          <button
                            className="rv-action-btn rv-action-btn--del"
                            title="Delete"
                            onClick={() => setConfirm({ id: v.vendorID, name: v.vendorName })}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              setPage={setPage}
              setPageSize={setPageSize}
              pageStart={pageStart}
              pageEnd={pageEnd}
              totalItems={totalItems}
            />
          </>
        )}
      </div>

      {/* ── Resize hint ── */}
      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint">
          <GripIcon /> Drag column borders to resize · Click a row to view details
        </p>
      )}
    </div>
  );
}

/* ── Icons ── */
function PlusIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function SearchIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function EditIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>; }
function TrashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function CheckIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>; }
function GripIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 13, height: 13 }}><circle cx="9" cy="5" r="1" fill="currentColor" /><circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="9" cy="19" r="1" fill="currentColor" /><circle cx="15" cy="5" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="19" r="1" fill="currentColor" /></svg>; }
function VendorIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M20 7H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /><line x1="12" y1="12" x2="12" y2="12" /><path d="M12 12v4" /></svg>; }
function BuildingIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18" /><path d="M9 21V9" /></svg>; }
function TruckIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="1" y="3" width="15" height="13" rx="1" /><path d="M16 8h4l3 5v4h-7V8z" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" /></svg>; }
function PhoneIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.9.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" /></svg>; }
function PhoneAltIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L7.91 8.91a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.9.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" /><line x1="15" y1="1" x2="15" y2="7" /><line x1="12" y1="4" x2="18" y2="4" /></svg>; }
function LocationIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>; }